import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import type {
  PartFileViewerPageData,
  ListCustomInterrogationsParams,
  CustomInterrogationListItem,
  InterrogationRequestBody,
  LegacyFileTaskStatusData,
} from '@frontend/api';

import { stockInterrogatingList } from 'modules/common/forms/materialWidget/data/actions';
import { cadViewer } from 'modules/common/viewer/cadViewer/data/actions';
import { partFileStatuses } from 'modules/viewerPage/data/actions';
import {
  getActiveFileUUID,
  getActivePartFile,
} from 'modules/viewerPage/data/reducer';

import partLibraryService from 'services/partLibraryService';

import { ActionWithPayload } from 'store/types';

import {
  types,
  geometricInterrogation,
  type SetWithInterrogationFilePayload,
  type ReceiveQueryPayload,
} from './actions';
import { getQuery, getSelectedResult } from './reducer';
import type {
  InterrogationSelection,
  LatentInterrogationSelection,
  InterrogationMaterialQuery,
} from './types';

export default function* root() {
  yield takeLatest(types.CLEAR, watchClear);
  yield takeLatest(types.RECEIVE_QUERY, watchReceiveQuery);
  yield takeLatest(
    types.SET_WITH_INTERROGATION_FILE,
    watchSetWithInterrogationFile,
  );
  yield takeLatest(types.SUBMIT, watchSubmit);
}

function* watchClear() {
  yield all([
    put(cadViewer.receiveInterrogationInfo({})),
    put(cadViewer.setGeometricEntities({})),
  ]);
}

function* watchReceiveQuery(
  action: ActionWithPayload<typeof types.RECEIVE_QUERY, ReceiveQueryPayload>,
) {
  const { material, task, shouldInit, useSelectedTask }: ReceiveQueryPayload =
    action.payload;

  let useTask = task;
  if (useSelectedTask) {
    const selectedResult: InterrogationSelection | null = yield select(
      getSelectedResult,
    );
    useTask = (selectedResult || {}).task || task;
  }

  try {
    const params: ListCustomInterrogationsParams = { task: useTask };
    if (material) {
      if (material[0]) params.materialClassId = material[0];
      if (material[1]) params.materialFamilyId = material[1];
      if (material[2]) params.materialId = material[2];
    }
    const uuid: string = yield select(getActiveFileUUID);
    const results: CustomInterrogationListItem[] = yield call(
      partLibraryService.listCustomInterrogations,
      {
        uuid,
        params,
      },
    );
    yield put(geometricInterrogation.receiveSearchResults({ results }));

    if (shouldInit) {
      yield call(collectDefaultState, results);
    } else {
      yield call(collectLatentState, results);
    }
  } catch (error) {
    console.error(error);
  }
}

function* collectDefaultState(results: CustomInterrogationListItem[]) {
  const nonDefaultResults = results.filter((r) => r.id);
  const defaultResults = results.filter((r) => !r.id);

  let result: CustomInterrogationListItem | null = null;
  for (const entry of nonDefaultResults.concat(defaultResults)) {
    if (entry.interrogationFileId) {
      result = entry;
      break;
    }
  }

  const { documentHash, interrogationFileId, task } = result || {};
  yield all([
    put(geometricInterrogation.receiveResultForDefault({ result })),
    put(
      cadViewer.receiveInterrogationInfo({
        documentHash,
        interrogationFileId,
        task,
      }),
    ),
    put(cadViewer.setGeometricEntities({})),
  ]);
}

function* collectLatentState(results: CustomInterrogationListItem[]) {
  // compare results against stored options in the result
  const selectedResult: InterrogationSelection | null = yield select(
    getSelectedResult,
  );
  if (!selectedResult) return;

  const nonDefaultResults = results.filter((r) => r.id);
  const defaultResults = results.filter((r) => !r.id);

  let result: CustomInterrogationListItem | null = null;
  for (const entry of nonDefaultResults.concat(defaultResults)) {
    if (entry.documentHash === selectedResult.documentHash) {
      result = entry;
      break;
    }
  }

  const { documentHash, interrogationFileId, task } = result || {};

  yield all([
    put(geometricInterrogation.receiveResultForDefault({ result })),
    put(
      cadViewer.receiveInterrogationInfo({
        documentHash,
        interrogationFileId,
        task,
      }),
    ),
    put(cadViewer.setGeometricEntities({})),
  ]);
}

function* watchSetWithInterrogationFile(
  action: ActionWithPayload<
    typeof types.SET_WITH_INTERROGATION_FILE,
    SetWithInterrogationFilePayload
  >,
) {
  const { payload } = action;
  const { partFileUuid, partInterrogationFile, showAdditionalKeysExplicit } =
    payload;

  const { id: interrogationFileId, documentHash, task } = partInterrogationFile;

  // try to collect the result form a custom interrogation. if none exist, show a fake
  // result with features without an associated interrogation file
  const params: ListCustomInterrogationsParams = { task };
  const results: CustomInterrogationListItem[] = yield call(
    partLibraryService.listCustomInterrogations,
    {
      uuid: partFileUuid,
      params,
    },
  );
  yield put(geometricInterrogation.receiveSearchResults({ results }));

  const nonDefaultResults = results.filter((r) => r.id);
  const defaultResults = results.filter((r) => !r.id);

  let result: InterrogationSelection | null = null;
  for (const entry of nonDefaultResults.concat(defaultResults)) {
    if (entry.interrogationFileId == interrogationFileId) {
      result = entry;
      break;
    }
  }

  if (!result) {
    const latent: LatentInterrogationSelection = {
      task,
      documentHash,
      interrogationFileId,
      name: task,
    };
    result = latent;
  }

  yield all([
    put(geometricInterrogation.receiveResultForDefault({ result })),
    put(
      cadViewer.receiveInterrogationInfo({
        documentHash: result.documentHash,
        interrogationFileId: result.interrogationFileId,
        task: result.task,
      }),
    ),
    put(
      cadViewer.setGeometricEntities({
        showAdditionalKeysExplicit,
      }),
    ),
  ]);
}

function* watchSubmit(
  action: ActionWithPayload<
    typeof types.SUBMIT,
    { result: CustomInterrogationListItem }
  >,
) {
  try {
    const { result } = action.payload;
    const {
      documentHash,
      id: customInterrogationId,
      interrogationFileId,
      task,
    } = result;

    yield put(
      cadViewer.receiveInterrogationInfo({
        documentHash,
        interrogationFileId,
        task,
      }),
    );

    const activePartFile: PartFileViewerPageData = yield select(
      getActivePartFile,
    );
    const { forbiddenInterrogationInformation, isDerivedFromInterrogation } =
      activePartFile;
    const forbiddenMessage = (
      forbiddenInterrogationInformation.find((info) => info.task === task) || {}
    ).forbiddenMessage;

    if (forbiddenMessage || isDerivedFromInterrogation) {
      yield put(cadViewer.setGeometricEntities({}));
    } else if (interrogationFileId) {
      yield put(cadViewer.setGeometricEntities({}));
    } else {
      yield call(interrogationRequest, {
        activePartFile,
        customInterrogationId,
        task,
      });
    }
  } catch (error) {
    console.error('There was an error during interrogation', error);
  }
}

type InterrogationRequestSagaArgs = {
  activePartFile: PartFileViewerPageData;
  customInterrogationId: number | null;
  task: string;
};

function* interrogationRequest({
  activePartFile,
  customInterrogationId,
  task,
}: InterrogationRequestSagaArgs) {
  // interrogation request
  try {
    const query: {
      material: InterrogationMaterialQuery | null;
      task: string | null;
    } = yield select(getQuery);
    const { material } = query;
    // material query is [classId, familyId, materialId]; only send when fully selected
    const materialId =
      material && material.length === 3 ? material[2] ?? null : null;
    const data: InterrogationRequestBody = {
      customInterrogationId,
      materialId,
      task,
    };
    const response: Record<string, LegacyFileTaskStatusData> = yield call(
      partLibraryService.interrogationRequest,
      {
        uuid: activePartFile.uuid,
        data,
      },
    );
    const nextInterrogationStatuses = {
      [activePartFile.uuid]: response,
    };
    yield put(
      partFileStatuses.receiveInterrogationStatuses({
        interrogationStatuses: nextInterrogationStatuses,
      }),
    );
    yield put(stockInterrogatingList.add({ uuid: activePartFile.uuid }));
  } catch (error) {
    console.error(error);
  }
}
